<script lang="ts" setup>
import { debounce } from '@antfu/utils';
import { defaultLocations } from '~/constants/defaultLocations';
import type { Location } from '~/types/Checkout';

defineProps<{ label: string }>();

const checkoutStore = useCheckoutStore();
const { selectedLocation } = storeToRefs(checkoutStore);

const isShowModal = ref(false);
const isLoading = ref(false);
const locationSearchQuery = ref('');
const locationsListInModal = ref(defaultLocations);
const searchLocationsList = ref([] as Location[]);

async function selectLocation(location: Location) {
  checkoutStore.setSelectedLocation(location);
  isShowModal.value = false;
}

async function searchLocations() {
  if (locationSearchQuery.value.length >= 2) {
    isLoading.value = true;
    searchLocationsList.value = await checkoutStore.searchLocations(locationSearchQuery.value);
    isLoading.value = false;
  } else {
    searchLocationsList.value = [];
  }
}

const debouncedLocationSearch = debounce(500, searchLocations);

watch(
  () => locationSearchQuery.value,
  () => debouncedLocationSearch(),
);

watch(
  () => searchLocationsList.value,
  () => {
    locationsListInModal.value = searchLocationsList.value.length ? searchLocationsList.value : defaultLocations;
  },
);

function getLocationName(location: Location) {
  const { data } = location;

  if (data.city) {
    if (data.settlement) {
      return `${data.settlement_type}. ${data.settlement}, ${data.city_type}. ${data.city}`;
    }

    return `${data.city_type}. ${data.city}`;
  }

  if (data.settlement) {
    return `${data.settlement_type}. ${data.settlement}`;
  }

  return '';
}
</script>

<template>
  <section class="location-select-block">
    <h3>
      {{ label }}

      <button
        class="text-left border-b-1 border-dashed text-blue transition hover:opacity-70"
        @click="isShowModal = true"
      >
        {{ getLocationName(selectedLocation) }}

        <SvgoChevronDown
          class="inline stroke-2"
          filled
        />
      </button>
    </h3>

    <BaseModal
      v-model="isShowModal"
      title="Выбор местоположения"
      @close="isShowModal = false"
    >
      <section class="flex flex-col gap-6 pb-3">
        <InputField
          v-model="locationSearchQuery"
          :is-loading="isLoading"
          :is-show-clear-btn="locationSearchQuery.length > 0"
          autofocus
          name="location-search"
          placeholder="Поиск"
        />

        <p class="text-xs sm:text-sm">
          От выбранного региона зависят доступные способы доставки, их стоимость и наличие товаров
        </p>

        <div class="location-select-block__list">
          <button
            v-for="(location, key) in locationsListInModal"
            :key="`${key}-${location.data.city_kladr_id ?? location.data.settlement_kladr_id}`"
            :class="{ 'font-bold': location.value === selectedLocation.value }"
            class="location-list-item"
            @click="selectLocation(location)"
          >
            <div v-html="highlightTextOccurrence(location.value, locationSearchQuery)" />

            <SvgoCheck
              v-if="location.value === selectedLocation.value"
              class="text-2xl"
              filled
            />
          </button>
        </div>
      </section>
    </BaseModal>
  </section>
</template>

<style lang="scss" scoped>
.location-select-block {
  display: block;

  &__list {
    display: flex;
    flex-shrink: 1;
    flex-direction: column;
    overflow-y: scroll;
    max-height: 300px;

    @include screen-sm {
      max-height: 380px;
    }

    @include screen-md {
      max-height: 450px;
    }
  }
}

.location-list-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid $black-10;
  font-size: $text-sm;
  line-height: 1.5;
  text-align: left;
  cursor: pointer;
  transition: color 0.3s;

  @include screen-md {
    font-size: $text-base;
  }

  &:hover {
    color: $black-40;
  }

  &:last-of-type {
    border: none;
  }
}
</style>
